/**
 * OnboardingWizard — 2-field Smart Flow.
 *
 * Phase A flow:
 *   1. Step 1/2: Company Website
 *   2. Step 2/2: Company Size
 *   3. Submit → POST /api/onboarding/setup, persist job_id, redirect to /dashboard
 *
 * The dashboard's AISetupBanner takes over from here:
 *   - Polls /api/onboarding/setup/status/{job_id}
 *   - Shows the same step list (Verifying → Reading → Analysing → Building ICP → Finding leads)
 *   - Fires MagicMomentModal + confetti when brain_data_seeded flips
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Users, CheckCircle2, ArrowRight, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'sonner';
import api from '../../lib/api';

// ────────────────────────────────────────────────────────────
// Step Definitions — only 2 fields. Region + Buyer Role are
// auto-inferred on the backend from the website.
// ────────────────────────────────────────────────────────────
const WIZARD_STEPS = [
  {
    key: 'website_url',
    label: 'Website',
    icon: Globe,
    title: "What's your company website?",
    subtitle: "We'll auto-detect your industry, primary sales region, and ideal buyer role from your site.",
    inputType: 'url',
    placeholder: 'e.g., acme.com or https://acme.com',
    required: true,
  },
  {
    key: 'team_size',
    label: 'Team',
    icon: Users,
    title: 'How big is your team?',
    subtitle: 'Helps us calibrate recommendations and pricing for your stage.',
    inputType: 'chips',
    chips: ['1-10', '11-50', '51-200', '201-500', '500+'],
    required: true,
  },
];

const normalizeUrl = (raw) => {
  if (!raw) return '';
  let v = String(raw).trim();
  if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
  return v;
};

const validateUrl = (raw) => {
  const norm = normalizeUrl(raw);
  try {
    const u = new URL(norm);
    return u.hostname.includes('.') && u.hostname.length >= 3;
  } catch {
    return false;
  }
};

// ────────────────────────────────────────────────────────────
// Step Pill (header breadcrumb)
// ────────────────────────────────────────────────────────────
function StepPill({ step, index, currentStep, isDark }) {
  const Icon = step.icon;
  const isActive = index === currentStep;
  const isDone = index < currentStep;
  return (
    <div className="flex items-center gap-2 shrink-0" data-testid={`step-pill-${step.key}`}>
      <div
        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300 ${
          isDone
            ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30'
            : isActive
            ? 'bg-primary shadow-lg shadow-primary/30 scale-110'
            : isDark ? 'bg-muted' : 'bg-blue-50'
        }`}
      >
        {isDone ? (
          <CheckCircle2 className="h-4 w-4 text-white" />
        ) : (
          <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
        )}
      </div>
      <span className={`text-xs font-semibold hidden sm:inline ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
        {step.label}
      </span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// MAIN WIZARD
// ────────────────────────────────────────────────────────────
export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const inputRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);

  // ── Already onboarded? skip straight to dashboard ──
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/onboarding/status');
        if (res.data?.brain_data_seeded) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (_) { /* silent — let the wizard render */ }
      // Iter 62.1 — pre-fill the website URL when the user re-ran onboarding
      // from the fallback banner (so they don't have to retype it).
      try {
        const prefill = localStorage.getItem('onboarding_prefill_url');
        if (prefill) {
          setInputValue(prefill);
          localStorage.removeItem('onboarding_prefill_url');
        }
      } catch (_) { /* ignore */ }
      setStatusChecked(true);
    })();
  }, [navigate]);

  useEffect(() => {
    if (inputRef.current && WIZARD_STEPS[currentStep]?.inputType === 'url') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    setInputValue('');
  }, [currentStep]);

  const step = WIZARD_STEPS[currentStep];

  const handleNext = (value) => {
    if (!step) return;
    const val = value !== undefined ? value : inputValue.trim();

    if (step.key === 'website_url' && !validateUrl(val)) {
      toast.error('Please enter a valid website URL (e.g., acme.com)');
      return;
    }

    const normalized = step.key === 'website_url' ? normalizeUrl(val) : val;
    const next = { ...answers, [step.key]: normalized || null };
    setAnswers(next);

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      submitOnboarding(next);
    }
  };

  // ── Phase A: redirect immediately after the API queues the job ──
  const submitOnboarding = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const payload = {
        website_url: finalAnswers.website_url,
        team_size: finalAnswers.team_size,
      };
      const res = await api.post('/api/onboarding/setup', payload);
      const newJobId = res.data?.job_id;
      if (!newJobId) throw new Error('No job_id from setup');

      // Hand the job_id to the dashboard banner.
      try {
        localStorage.setItem('onboarding_job_id', newJobId);
        localStorage.setItem('onboarding_just_completed', 'true');
      } catch {}

      // Drop the user straight on /dashboard. AISetupBanner handles the rest.
      navigate('/dashboard', { replace: true });
    } catch (e) {
      console.error('Setup failed:', e);
      toast.error('Setup failed — redirecting to dashboard');
      setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && step?.inputType === 'url') handleNext();
  };

  if (!statusChecked) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-background' : 'bg-gradient-to-br from-blue-50 via-white to-violet-50'}`}>
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div
      className={`relative min-h-screen flex flex-col overflow-hidden ${
        isDark ? 'bg-background' : 'bg-gradient-to-br from-blue-50 via-white to-violet-50'
      }`}
      data-testid="onboarding-wizard"
    >
      {/* Ambient glow */}
      {isDark && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full opacity-[0.18] blur-3xl"
            style={{ background: 'radial-gradient(circle, hsl(215, 100%, 55%) 0%, transparent 65%)' }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[-200px] right-[-120px] h-[460px] w-[540px] rounded-full opacity-[0.10] blur-3xl"
            style={{ background: 'radial-gradient(circle, hsl(265, 90%, 60%) 0%, transparent 65%)' }}
          />
        </>
      )}

      {/* Header — stepper + theme toggle. No skip. */}
      <div className={`relative border-b sticky top-0 z-10 backdrop-blur-md ${isDark ? 'bg-background/70 border-border/60' : 'bg-white/70 border-blue-100'}`}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            {WIZARD_STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                <StepPill step={s} index={i} currentStep={currentStep} isDark={isDark} />
                {i < WIZARD_STEPS.length - 1 && (
                  <div className={`h-px w-4 sm:w-8 ${i < currentStep ? 'bg-emerald-400' : isDark ? 'bg-border' : 'bg-blue-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Step body */}
      <div className="relative flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-xl animate-[fadeIn_0.3s_ease-out]" key={currentStep}>
          <div className={`rounded-3xl border shadow-2xl p-8 sm:p-10 ${
            isDark
              ? 'bg-card/70 border-border/60 backdrop-blur-xl shadow-black/40'
              : 'bg-white/90 border-blue-100 backdrop-blur-md shadow-blue-200/30'
          }`}>
            <div className="text-center mb-7">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-blue-500 shadow-xl shadow-primary/30 mb-4">
                {step && <step.icon className="h-7 w-7 text-white" />}
              </div>
              <div className="text-[11px] font-medium text-muted-foreground tracking-wide mb-2 uppercase">
                Step {currentStep + 1} of {WIZARD_STEPS.length} · ~60 seconds
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 tracking-tight" data-testid="step-title">
                {step?.title}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{step?.subtitle}</p>
            </div>

            {step?.inputType === 'url' && (
              <div className="space-y-3" data-testid="url-input-group">
                <Input
                  ref={inputRef}
                  name={step.key}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={step.placeholder}
                  className={`h-14 text-base rounded-2xl border-2 transition-colors ${
                    isDark
                      ? 'bg-background/60 border-border focus-visible:border-primary focus-visible:ring-primary/20'
                      : 'bg-white border-blue-100 focus-visible:border-primary focus-visible:ring-primary/20'
                  }`}
                  data-testid={`onboarding-${step.key}-input`}
                  disabled={submitting}
                />
                <p className="text-[11px] text-muted-foreground/80 px-1">
                  We only read public pages — never store credentials or private data.
                </p>
                <Button
                  onClick={() => handleNext()}
                  className="w-full h-12 rounded-2xl bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90 text-white font-semibold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:hover:scale-100"
                  disabled={!inputValue.trim() || submitting}
                  data-testid="onboarding-continue-btn"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Setting up…</> : <>Continue <ArrowRight className="h-4 w-4 ml-1" /></>}
                </Button>
              </div>
            )}

            {step?.inputType === 'chips' && (
              <div className="space-y-4" data-testid="chips-input-group">
                <div className="flex flex-wrap gap-3 justify-center">
                  {step.chips.map((chip) => (
                    <button
                      key={chip}
                      onClick={() => handleNext(chip)}
                      disabled={submitting}
                      className={`px-5 py-3 rounded-2xl font-semibold text-base transition-all duration-200 hover:scale-105 active:scale-95 shadow-md border disabled:opacity-50 disabled:hover:scale-100 ${
                        isDark
                          ? 'bg-background/60 border-border hover:border-primary text-foreground hover:shadow-primary/30 hover:shadow-lg'
                          : 'bg-white border-blue-100 hover:border-primary text-foreground hover:shadow-primary/20 hover:shadow-xl'
                      }`}
                      data-testid={`wizard-chip-${chip.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
                {submitting && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="wizard-submitting">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Taking you to your CRM…
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-center text-[11px] text-muted-foreground/60 mt-5">
            Your data is encrypted. You can edit anything in Settings later.
          </p>
        </div>
      </div>
    </div>
  );
}
